import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { StatusPill, UndeliveredFlag } from "../../components/founder/ui";
import BusinessActions from "../../components/founder/BusinessActions";
import { Search, Wrench, ClipboardCheck, FileText, Users, ArrowUpRight, FileCheck } from "lucide-react";

const ICONS = { assessment_ready: FileCheck, investigation: Search, review: ClipboardCheck, intake: FileText, followup: Users, opportunity: Wrench };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const reload = () => api.get("/dashboard").then((r) => setData(r.data));
  useEffect(() => { reload(); }, []);

  if (!data) return <div className="p-8 text-lr-leather">Loading…</div>;

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-lr-charcoal">Today</h1>
          <p className="text-lr-leather mt-1">What needs your attention.</p>
        </div>
      </div>

      <section data-testid="today-panel" className="mb-10">
        {data.today.length === 0 ? (
          <div className="lr-card p-6 text-lr-leather">Nothing urgent right now. Good time to prospect.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {data.today.map((t, i) => {
              const Icon = ICONS[t.kind] || Search;
              return (
                <button key={i} onClick={() => navigate("/app/businesses")} data-testid={`today-${t.kind}`}
                  className="lr-card p-5 flex items-center gap-4 text-left hover:border-lr-olive/50 transition-colors">
                  <span className="h-11 w-11 rounded-[4px] bg-lr-clay/12 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-lr-clay" />
                  </span>
                  <span className="text-lr-charcoal">{t.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="mb-10">
        <h2 className="font-serif text-2xl text-lr-charcoal mb-4">Active Blueprints</h2>
        {data.active_blueprints.length === 0 ? (
          <div className="lr-card p-6 text-lr-leather">No active engagements yet.</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {data.active_blueprints.map((b) => (
              <div key={b.id} onClick={() => navigate(`/app/businesses/${b.id}`)} data-testid={`active-card-${b.id}`}
                role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate(`/app/businesses/${b.id}`)}
                className="lr-card p-5 text-left hover:border-lr-olive/50 transition-colors group cursor-pointer">
                <div className="flex items-start justify-between">
                  <h3 className="font-serif text-lg text-lr-charcoal leading-tight">{b.business_name}</h3>
                  <div className="flex items-center gap-1">
                    <BusinessActions business={b} onChange={reload} />
                    <ArrowUpRight className="h-4 w-4 text-lr-leather group-hover:text-lr-olive transition-colors" />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StatusPill status={b.status} />
                  <UndeliveredFlag status={b.status} />
                  {b.is_demo && <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded-[3px] bg-lr-leather/15 text-lr-leather">DEMO</span>}
                </div>
                {b.primary_trade && <p className="text-sm text-lr-leather mt-3">{b.primary_trade} · {b.location || "Not recorded"}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-serif text-2xl text-lr-charcoal mb-4">Pipeline</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.entries(data.pipeline).map(([k, v]) => (
            <div key={k} className="lr-card p-4 text-center" data-testid={`pipeline-${k}`}>
              <div className="font-serif text-3xl text-lr-olive">{v}</div>
              <div className="text-xs text-lr-leather mt-1 uppercase tracking-wide">{k}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
